import React, { useState, useEffect } from 'react';
import { getRecognitions, getUserBadgeProgress } from '../services/api';
import RecognitionCard from '../components/RecognitionCard';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Building2, MapPin, Loader2, Heart, MessageCircle, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

function Profile({ user }) {
  const { language } = useLanguage();
  const [received, setReceived] = useState([]);
  const [given, setGiven] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received');

  useEffect(() => {
    loadRecognitions();
  }, [user.id]);

  async function loadRecognitions() {
    setLoading(true);
    try {
      const [receivedRes, givenRes, progressRes] = await Promise.all([
        getRecognitions({ receiver_id: user.id, limit: 50 }),
        getRecognitions({ giver_id: user.id, limit: 50 }),
        getUserBadgeProgress(user.id)
      ]);
      setReceived(receivedRes.data || []);
      setGiven(givenRes.data || []);
      setBadgeProgress(progressRes.data || null);
    } catch (err) {
      console.error('Failed to load recognitions:', err);
    } finally {
      setLoading(false);
    }
  }

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Calculate stats
  const totalLikesReceived = received.reduce((sum, r) => sum + (r.like_count || 0), 0);
  const totalCommentsReceived = received.reduce((sum, r) => sum + (r.comment_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-stretch gap-6">
            <Avatar className="h-24 w-24 ring-4 ring-primary/20 ring-offset-2 self-center">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left flex flex-col justify-center">
              <h1 className="text-2xl font-bold text-foreground">{user.name}</h1>
              <div className="mt-2 flex flex-wrap justify-center sm:justify-start items-center gap-4 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  {user.department}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {user.location}
                </span>
              </div>

              {/* Stats */}
              <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{badgeProgress?.totalReceived || received.length}</p>
                  <p className="text-xs text-muted-foreground">{t('received', language)}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{badgeProgress?.totalGiven || given.length}</p>
                  <p className="text-xs text-muted-foreground">{t('given', language)}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{totalLikesReceived}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {t('likes', language)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{totalCommentsReceived}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" /> {t('comments', language)}
                  </p>
                </div>
              </div>
            </div>

            {/* Badges Grid with Progress */}
            {badgeProgress && (
              <div className="flex-1 flex items-center justify-center gap-4">
                {/* Badge Grid */}
                <div className="flex flex-col items-center justify-center gap-1">
                {/* Row 1: Core Values (Poker Chip Style) */}
                <div className="flex justify-center gap-1">
                  {['collaborative', 'dedicated', 'entrepreneurial', 'fun'].map((valueName) => {
                    const valueData = badgeProgress.values.find(v => v.name === valueName) || { count: 0, threshold: 3, earned: false };
                    const progress = Math.min((valueData.count / valueData.threshold) * 100, 100);
                    const colors = { c: '#3b82f6', d: '#22c55e', e: '#f59e0b', f: '#ec4899' };
                    const color = colors[valueName.charAt(0)];
                    const grayColor = '#d1d5db';
                    const label = valueName.charAt(0).toUpperCase();
                    const gradientId = `header-chip-gradient-${valueName}`;
                    const displayName = language === 'zh' && valueData.display_name_chinese ? valueData.display_name_chinese : (valueData.display_name || valueName.charAt(0).toUpperCase() + valueName.slice(1));
                    const tooltip = `${displayName} - ${valueData.count}/${valueData.threshold}`;
                    const stripeColor = progress >= 100 ? 'white' : '#b0b0b0';
                    return (
                      <div key={valueName} className="relative group" style={{ display: 'inline-block', cursor: 'pointer' }}>
                        <svg width="36" height="36" viewBox="0 0 44 44">
                          <defs>
                            <linearGradient id={gradientId} x1="0%" y1="100%" x2="0%" y2="0%">
                              <stop offset={`${progress}%`} stopColor={color} />
                              <stop offset={`${progress}%`} stopColor={grayColor} />
                            </linearGradient>
                          </defs>
                          {/* Outer ring with notches */}
                          <circle cx="22" cy="22" r="20" fill={`url(#${gradientId})`} stroke={progress >= 100 ? color : '#a0a0a0'} strokeWidth="2" />
                          {/* Notches around edge */}
                          {[...Array(16)].map((_, i) => {
                            const angle = (i * 22.5) * Math.PI / 180;
                            const x1 = 22 + Math.cos(angle) * 17;
                            const y1 = 22 + Math.sin(angle) * 17;
                            const x2 = 22 + Math.cos(angle) * 20;
                            const y2 = 22 + Math.sin(angle) * 20;
                            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stripeColor} strokeWidth="2" />;
                          })}
                          {/* Inner circle */}
                          <circle cx="22" cy="22" r="13" fill={progress >= 100 ? color : grayColor} stroke={stripeColor} strokeWidth="1.5" />
                          {/* Letter */}
                          <text x="22" y="27" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                            {label}
                          </text>
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                          {tooltip}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Row 2: Fundamentals 1-10 */}
                <div className="flex justify-center gap-1">
                  {[1,2,3,4,5,6,7,8,9,10].map((num) => {
                    const fundData = badgeProgress.fundamentals.find(f => f.name === `fundamental_${num}`) || { count: 0, threshold: 3, earned: false };
                    const progress = Math.min((fundData.count / fundData.threshold) * 100, 100);
                    const gradientId = `header-fund-gradient-${num}`;
                    const displayName = language === 'zh' && fundData.display_name_chinese ? fundData.display_name_chinese : (fundData.display_name || `Fundamental #${num}`);
                    const tooltip = `${displayName} - ${fundData.count}/${fundData.threshold}`;
                    return (
                      <div key={num} className="relative group" style={{ display: 'inline-block', cursor: 'pointer' }}>
                        <svg width="32" height="36" viewBox="0 0 28 32">
                          <defs>
                            <linearGradient id={gradientId} x1="0%" y1="100%" x2="0%" y2="0%">
                              <stop offset={`${progress}%`} stopColor="#f59e0b" />
                              <stop offset={`${progress}%`} stopColor="#d1d5db" />
                            </linearGradient>
                          </defs>
                          <path
                            d="M14 0 C6 0, 0 2, 0 6 L0 18 C0 24, 14 32, 14 32 C14 32, 28 24, 28 18 L28 6 C28 2, 22 0, 14 0 Z"
                            fill={`url(#${gradientId})`}
                          />
                          <text x="14" y="18" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
                            {num}
                          </text>
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                          {tooltip}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Row 3: Fundamentals 11-20 */}
                <div className="flex justify-center gap-1">
                  {[11,12,13,14,15,16,17,18,19,20].map((num) => {
                    const fundData = badgeProgress.fundamentals.find(f => f.name === `fundamental_${num}`) || { count: 0, threshold: 3, earned: false };
                    const progress = Math.min((fundData.count / fundData.threshold) * 100, 100);
                    const gradientId = `header-fund-gradient-${num}`;
                    const displayName = language === 'zh' && fundData.display_name_chinese ? fundData.display_name_chinese : (fundData.display_name || `Fundamental #${num}`);
                    const tooltip = `${displayName} - ${fundData.count}/${fundData.threshold}`;
                    return (
                      <div key={num} className="relative group" style={{ display: 'inline-block', cursor: 'pointer' }}>
                        <svg width="32" height="36" viewBox="0 0 28 32">
                          <defs>
                            <linearGradient id={gradientId} x1="0%" y1="100%" x2="0%" y2="0%">
                              <stop offset={`${progress}%`} stopColor="#f59e0b" />
                              <stop offset={`${progress}%`} stopColor="#d1d5db" />
                            </linearGradient>
                          </defs>
                          <path
                            d="M14 0 C6 0, 0 2, 0 6 L0 18 C0 24, 14 32, 14 32 C14 32, 28 24, 28 18 L28 6 C28 2, 22 0, 14 0 Z"
                            fill={`url(#${gradientId})`}
                          />
                          <text x="14" y="18" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
                            {num}
                          </text>
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                          {tooltip}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Row 4: Fundamentals 21-30 */}
                <div className="flex justify-center gap-1">
                  {[21,22,23,24,25,26,27,28,29,30].map((num) => {
                    const fundData = badgeProgress.fundamentals.find(f => f.name === `fundamental_${num}`) || { count: 0, threshold: 3, earned: false };
                    const progress = Math.min((fundData.count / fundData.threshold) * 100, 100);
                    const gradientId = `header-fund-gradient-${num}`;
                    const displayName = language === 'zh' && fundData.display_name_chinese ? fundData.display_name_chinese : (fundData.display_name || `Fundamental #${num}`);
                    const tooltip = `${displayName} - ${fundData.count}/${fundData.threshold}`;
                    return (
                      <div key={num} className="relative group" style={{ display: 'inline-block', cursor: 'pointer' }}>
                        <svg width="32" height="36" viewBox="0 0 28 32">
                          <defs>
                            <linearGradient id={gradientId} x1="0%" y1="100%" x2="0%" y2="0%">
                              <stop offset={`${progress}%`} stopColor="#f59e0b" />
                              <stop offset={`${progress}%`} stopColor="#d1d5db" />
                            </linearGradient>
                          </defs>
                          <path
                            d="M14 0 C6 0, 0 2, 0 6 L0 18 C0 24, 14 32, 14 32 C14 32, 28 24, 28 18 L28 6 C28 2, 22 0, 14 0 Z"
                            fill={`url(#${gradientId})`}
                          />
                          <text x="14" y="18" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
                            {num}
                          </text>
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                          {tooltip}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recognitions Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="received" className="gap-2">
            {t('received', language)} ({badgeProgress?.totalReceived || received.length})
          </TabsTrigger>
          <TabsTrigger value="given" className="gap-2">
            {t('given', language)} ({badgeProgress?.totalGiven || given.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="space-y-4">
          {received.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {t('noRecognitionsReceived', language)}
                </h3>
                <p className="text-muted-foreground">
                  {t('keepUpGreatWork', language)}
                </p>
              </CardContent>
            </Card>
          ) : (
            received.map(recognition => (
              <RecognitionCard
                key={recognition.id}
                recognition={recognition}
                currentUser={user}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="given" className="space-y-4">
          {given.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {t('noRecognitionsGiven', language)}
                </h3>
                <p className="text-muted-foreground">
                  {t('startRecognizing', language)}
                </p>
              </CardContent>
            </Card>
          ) : (
            given.map(recognition => (
              <RecognitionCard
                key={recognition.id}
                recognition={recognition}
                currentUser={user}
              />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Profile;
